import { UserDto } from '../user';

export enum AuScope {
  OAuth = 'integrations.oauth',
  TestData = 'integrations.test-data',
  TwilioAdmin = 'integrations.twilio.admin',
  JobQueuesAdmin = 'job-queues.admin',
  Test = 'test',
  OrganizationSetToken = 'org.settoken',
  UsersCreate = 'users.create',
  UsersRead = 'users.read',
  UsersUpdate = 'users.update',
  UsersDelete = 'users.delete',
  ExamplesCreate = 'examples.create',
  ExamplesRead = 'examples.read',
  ExamplesUpdate = 'examples.update',
  ExamplesDelete = 'examples.delete',
  DashboardCreate = 'dashboard.create',
  DashboardRead = 'dashboard.read',
  DashboardUpdate = 'dashboard.update',
  DashboardDelete = 'dashboard.delete',
  ConversionRead = 'conversion.read',
  ConversionCreate = 'conversion.create',
  ConversionUpdate = 'conversion.update',
  ConversionDelete = 'conversion.delete',
  ConversionFilterListRead = 'conversion-filter-list.read',
  ConversionFilterListCreate = 'conversion-filter-list.create',
  ConversionFilterListUpdate = 'conversion-filter-list.update',
  ConversionFilterListDelete = 'conversion-filter-list.delete',
  ConversionOutputStreamRead = 'conversion-output-stream.read',
  ConversionOutputStreamCreate = 'conversion-output-stream.create',
  ConversionOutputStreamUpdate = 'conversion-output-stream.update',
  ConversionOutputStreamDelete = 'conversion-output-stream.delete',
  ConversionOutputStreamConsumptionEventRead = 'conversion-output-stream-consumption-event.read',
  ConversionOutputStreamConsumptionEventCreate = 'conversion-output-stream-consumption-event.create',
  ConversionOutputStreamConsumptionEventUpdate = 'conversion-output-stream-consumption-event.update',
  ConversionOutputStreamConsumptionEventDelete = 'conversion-output-stream-consumption-event.delete',
  TrackingCreate = 'tracking.create',
  TrackingRead = 'tracking.read',
  TrackingUpdate = 'tracking.update',
  TrackingDelete = 'tracking.delete',
  FunnelCreate = 'funnel.create',
  FunnelRead = 'funnel.read',
  FunnelUpdate = 'funnel.update',
  FunnelDelete = 'funnel.delete',
  RetainerRead = 'retainer.read',
  RetainerCreate = 'retainer.create',
  RetainerUpdate = 'retainer.update',
  RetainerDelete = 'retainer.delete',
  ConversionInputStreamRead = 'conversion-input-stream.read',
  ConversionInputStreamCreate = 'conversion-input-stream.create',
  ConversionInputStreamUpdate = 'conversion-input-stream.update',
  ConversionInputStreamDelete = 'conversion-input-stream.delete',
  OrgConfigRead = 'org-config.read',
  OrgConfigUpdate = 'org-config.update',
  /** View the published brand kit. */
  BrandKitRead = 'brand-kit.read',
  /** Edit the draft, publish, and restore brand kit versions. */
  BrandKitUpdate = 'brand-kit.update',
  /** Use the platform MCP server at all. Each tool also checks its own scope. */
  McpAccess = 'mcp.access',
  AdAccountRead = 'ad-account.read',
  AdAccountCreate = 'ad-account.create',
  AdAccountUpdate = 'ad-account.update',
  AdAccountDelete = 'ad-account.delete',
  AdDataStreamRead = 'ad-data-stream.read',
  AdDataStreamCreate = 'ad-data-stream.create',
  AdDataStreamUpdate = 'ad-data-stream.update',
  AdDataStreamDelete = 'ad-data-stream.delete',
  IntegrationConnectionRead = 'integration-connection.read',
  IntegrationConnectionCreate = 'integration-connection.create',
  IntegrationConnectionUpdate = 'integration-connection.update',
  IntegrationConnectionDelete = 'integration-connection.delete',
  /** Read the ad platforms' own performance numbers, campaigns and ads (over MCP). */
  AdPlatformPerformanceRead = 'ad-platform-performance.read',
  /** Read marketing performance: platform spend with Arrows Up's attributed results (over MCP). */
  MarketingPerformanceRead = 'marketing-performance.read',
  /** Read synced ad account settings, goals and change history (over MCP). */
  AdSettingsRead = 'ad-settings.read',
}

/**
 * Scopes no policy but `AuPolicy.AuAdmin` may grant. Our platform credentials
 * (Google MCC, Microsoft manager customer, Meta Business Manager) reach every
 * client's ad accounts, so anyone who can discover or add an ad account, or
 * sync one, could pull another client's ad data into their own account.
 * Connections hold clients' own vendor keys, which only AU admins manage.
 */
export const AuAdminOnlyScopes: readonly AuScope[] = [
  AuScope.AdAccountRead,
  AuScope.AdAccountCreate,
  AuScope.AdAccountUpdate,
  AuScope.AdAccountDelete,
  AuScope.AdDataStreamRead,
  AuScope.AdDataStreamCreate,
  AuScope.AdDataStreamUpdate,
  AuScope.AdDataStreamDelete,
  AuScope.IntegrationConnectionRead,
  AuScope.IntegrationConnectionCreate,
  AuScope.IntegrationConnectionUpdate,
  AuScope.IntegrationConnectionDelete,
];

/**
 * Scope bundles for a given purpose.
 *
 * Do not change, used in DB.
 */
export enum AuPolicy {
  AuAdmin = 'au-admin',
  Test = 'test',
  WebClientUser = 'web-client-user',
  WebClientAdmin = 'web-client-admin',
  BrandUser = 'brand-user',
  BrandManager = 'brand-manager',
  /** Reads ad platform performance, ad settings and marketing performance over MCP. */
  AdAnalyst = 'ad-analyst',
}

const brandUserScopes = [AuScope.BrandKitRead, AuScope.McpAccess];

export const AuPolicyMap: Record<AuPolicy, AuScope[]> = {
  [AuPolicy.Test]: [
    AuScope.Test,
    AuScope.ExamplesCreate,
    AuScope.ExamplesRead,
    AuScope.ExamplesUpdate,
    AuScope.ExamplesDelete,
  ],
  [AuPolicy.AuAdmin]: [
    AuScope.JobQueuesAdmin,
    AuScope.TestData,
    AuScope.OrganizationSetToken,
    AuScope.TwilioAdmin,
    AuScope.DashboardCreate,
    AuScope.DashboardRead,
    AuScope.DashboardUpdate,
    AuScope.DashboardDelete,
    AuScope.UsersCreate,
    AuScope.UsersRead,
    AuScope.UsersUpdate,
    AuScope.UsersDelete,
    AuScope.ConversionCreate,
    AuScope.ConversionRead,
    AuScope.ConversionUpdate,
    AuScope.ConversionDelete,
    AuScope.ConversionFilterListRead,
    AuScope.ConversionFilterListCreate,
    AuScope.ConversionFilterListUpdate,
    AuScope.ConversionFilterListDelete,
    AuScope.ConversionOutputStreamRead,
    AuScope.ConversionOutputStreamCreate,
    AuScope.ConversionOutputStreamUpdate,
    AuScope.ConversionOutputStreamDelete,
    AuScope.ConversionOutputStreamConsumptionEventRead,
    AuScope.ConversionOutputStreamConsumptionEventCreate,
    AuScope.ConversionOutputStreamConsumptionEventUpdate,
    AuScope.ConversionOutputStreamConsumptionEventDelete,
    AuScope.ConversionInputStreamRead,
    AuScope.ConversionInputStreamCreate,
    AuScope.ConversionInputStreamUpdate,
    AuScope.ConversionInputStreamDelete,
    AuScope.TrackingCreate,
    AuScope.TrackingRead,
    AuScope.TrackingUpdate,
    AuScope.TrackingDelete,
    AuScope.FunnelCreate,
    AuScope.FunnelRead,
    AuScope.FunnelUpdate,
    AuScope.FunnelDelete,
    AuScope.RetainerCreate,
    AuScope.RetainerRead,
    AuScope.RetainerUpdate,
    AuScope.RetainerDelete,
    AuScope.OrgConfigRead,
    AuScope.OrgConfigUpdate,
    AuScope.McpAccess,
    AuScope.BrandKitRead,
    AuScope.BrandKitUpdate,
    AuScope.AdAccountRead,
    AuScope.AdAccountCreate,
    AuScope.AdAccountUpdate,
    AuScope.AdAccountDelete,
    AuScope.AdDataStreamRead,
    AuScope.AdDataStreamCreate,
    AuScope.AdDataStreamUpdate,
    AuScope.AdDataStreamDelete,
    AuScope.IntegrationConnectionRead,
    AuScope.IntegrationConnectionCreate,
    AuScope.IntegrationConnectionUpdate,
    AuScope.IntegrationConnectionDelete,
    AuScope.AdPlatformPerformanceRead,
    AuScope.MarketingPerformanceRead,
    AuScope.AdSettingsRead,
  ],
  [AuPolicy.WebClientAdmin]: [
    AuScope.OAuth,
    AuScope.UsersRead,
    AuScope.DashboardRead,
    AuScope.ConversionCreate,
    AuScope.ConversionRead,
    AuScope.ConversionUpdate,
    AuScope.ConversionDelete,
    AuScope.ConversionFilterListRead,
    AuScope.ConversionOutputStreamRead,
    AuScope.ConversionOutputStreamConsumptionEventRead,
    AuScope.ConversionInputStreamRead,
    AuScope.TrackingRead,
    AuScope.FunnelRead,
  ],
  [AuPolicy.WebClientUser]: [
    AuScope.UsersRead,
    AuScope.DashboardRead,
    AuScope.ConversionRead,
    AuScope.ConversionFilterListRead, // Conversion page needs this, too.
  ],
  [AuPolicy.BrandUser]: brandUserScopes,
  [AuPolicy.BrandManager]: [...brandUserScopes, AuScope.BrandKitUpdate],
  [AuPolicy.AdAnalyst]: [
    AuScope.AdPlatformPerformanceRead,
    AuScope.MarketingPerformanceRead,
    AuScope.AdSettingsRead,
    AuScope.McpAccess,
  ],
};

export interface AuAuthResp {
  access_token: string;
}

/** Payload of AuAuthResp.access_token. */
export interface AuJwtPayload {
  sub: string; // email
  auUser: UserDto;
}
